import React, { useEffect, useState } from 'react';
import { ConfirmationModal, RoadblockModal } from '@kaizen/draft-modal';
import { Box } from '@kaizen/component-library';
import { Paragraph, Heading } from '@kaizen/typography';
import { InlineNotification } from '@kaizen/notification';
import { UnansweredMandatoryQuestionsError } from 'ca-capture/SubmissionFinalize/errors';
import { PropSetter } from 'ca-dom-binding/types';

type UiText = {
  finalizeHeader: string;
  finalizeSubHeader: string;
  finalizeContent: string;
  finalizeDismissLabel: string;
  finalizeConfirmLabel: string;
  finalizeConfirmWorkingLabel: string;
  finalizeNotificationTitle: string;
  finalizeNotificationContent: string;
  errorHeader: string;
  errorContent: string;
  errorDismissLabel: string;
};

type Props = {
  isOpen: boolean;
  onDismiss: () => void;
  onSubmit: () => Promise<void>;
  onUnansweredMandatoryQuestionsError: () => void;
  onErrorDismiss: () => void;
  uiText: UiText;
  waitedTooLongTimeout?: number;
};

const WAITED_TOO_LONG_TIMEOUT = 10 * 1000;

export function SubmissionFinalize({
  isOpen = false,
  onDismiss,
  onSubmit,
  onUnansweredMandatoryQuestionsError,
  onErrorDismiss,
  uiText,
  waitedTooLongTimeout = WAITED_TOO_LONG_TIMEOUT,
}: Props) {
  const [showModal, setShowModal] = useState<boolean>(isOpen);
  const [isWorking, setIsWorking] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [hasWaitedTooLong, setHasWaitedTooLong] = useState<boolean>(false);

  useEffect(() => {
    setShowModal(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (hasError) {
      setShowModal(false);
      setIsWorking(false);
      setHasWaitedTooLong(false);
    }
  }, [hasError]);

  async function onConfirm() {
    setIsWorking(true);

    const submitTimeout = window.setTimeout(() => {
      setHasWaitedTooLong(true);
    }, waitedTooLongTimeout);

    try {
      await onSubmit();
    } catch (err) {
      if (err instanceof UnansweredMandatoryQuestionsError) {
        onUnansweredMandatoryQuestionsError();
        setIsWorking(false);
        setHasWaitedTooLong(false);
      } else {
        // Handle internally
        setHasError(true);
      }
    } finally {
      window.clearTimeout(submitTimeout);
    }
  }

  return (
    <>
      <RoadblockModal
        isOpen={hasError}
        title={uiText.errorHeader}
        onDismiss={() => {
          onErrorDismiss();
          setHasError(false);
        }}
        dismissLabel={uiText.errorDismissLabel}
        automationId="SubmissionFinalize-RoadblockModal"
      >
        <Paragraph variant="body">{uiText.errorContent}</Paragraph>
      </RoadblockModal>
      <ConfirmationModal
        automationId="SubmissionFinalize-ConfirmationModal"
        isOpen={showModal}
        mood="informative"
        isProminent={false}
        title={uiText.finalizeHeader}
        confirmLabel={uiText.finalizeConfirmLabel}
        dismissLabel={uiText.finalizeDismissLabel}
        onConfirm={onConfirm}
        onDismiss={onDismiss}
        confirmWorking={
          isWorking ? { label: uiText.finalizeConfirmWorkingLabel } : undefined
        }
      >
        <div>
          {hasWaitedTooLong && (
            <InlineNotification
              type="cautionary"
              title={uiText.finalizeNotificationTitle}
              persistent
              automationId="SubmissionFinalize-still-processing-notification"
            >
              {uiText.finalizeNotificationContent}
            </InlineNotification>
          )}
          <Box mb={1}>
            <Heading variant="heading-3">{uiText.finalizeSubHeader}</Heading>
          </Box>
          <Paragraph variant="body">{uiText.finalizeContent}</Paragraph>
        </div>
      </ConfirmationModal>
    </>
  );
}

type RegisterCallback = (arg: PropSetter<any>) => void;

export function SubmissionFinalizeDOMWrapper({
  registerPropsSetterCallback,
  initialProps,
}: {
  registerPropsSetterCallback: RegisterCallback;
  initialProps: any;
}) {
  const [props, setProps] = useState<any>(initialProps);
  useEffect(() => {
    registerPropsSetterCallback((partialProps) => {
      setProps({ ...props, ...partialProps });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setProps, registerPropsSetterCallback]);
  return <SubmissionFinalize {...props} />;
}
